import { IMPORT_PAGE_MAX, type ImportReviewItem, type ImportRunDetail } from '@enp/domain/api';
import { formatCount } from '@enp/domain/format';
import { parseMoney } from '@enp/domain/money';
import type { ProductRole } from '@enp/domain/catalogue';
import type { ManualDecision, ReviewKind } from '@enp/domain/migration';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  EmptyState,
  focusRing,
  formatMoney,
  Input,
  LoadingSkeleton,
  PageHeader,
  toast,
} from '@enp/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  CheckCheck,
  CircleCheck,
  History,
  Keyboard,
  Loader2,
  Search,
  Undo2,
} from 'lucide-react';
import {
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { z } from 'zod';
import { REVIEW_KINDS } from '@enp/domain/migration';
import {
  apiErrorText,
  ImportsBreadcrumb,
  Note,
  runLabel,
  useImportPermissions,
} from '../../../../../imports/components.js';
import {
  canPick,
  decisionInput,
  decisionOptions,
  describeDecision,
  namesOf,
  suggestionBreakdown,
  suggestionOf,
  type DecisionInput,
  type DecisionOption,
  type PickTarget,
} from '../../../../../imports/decisions.js';
import { ROLE_LABEL, ROLE_ORDER } from '../../../../../catalogue/labels.js';
import { REVIEW_KIND, REVIEW_ORDER, reviewItemText } from '../../../../../imports/labels.js';
import {
  categoriesQuery,
  DecisionBatchError,
  importKeys,
  invalidateRun,
  isBusy,
  itemSearchQuery,
  outcomesByRefQuery,
  postDecisions,
  reviewItemsQuery,
  rulesQuery,
  runQuery,
} from '../../../../../imports/queries.js';
import { componentTargetsQuery } from '../../../../../production/queries.js';
import { useAccountTimeZone } from '../../../../../shell/account-date.js';
import { RoomForStickyBar, useDocumentTitle } from '../../../../../shell/page.js';
import { ErrorPanel } from '../../../../../shell/route-states.js';
import { isTypingTarget } from '../../../../../shell/shortcuts.js';

const searchSchema = z.object({
  kind: z.enum(REVIEW_KINDS).optional().catch(undefined),
  /** `decided`: the decisions stored earlier (to take one back). */
  view: z.enum(['decided']).optional().catch(undefined),
});

export const Route = createFileRoute('/_app/imports/runs/$runId/review')({
  validateSearch: searchSchema,
  component: ReviewPage,
});

interface Pending {
  label: string;
  decisions: ManualDecision[];
}

const RECORD_KIND = { item: 'Item', variant: 'Variant', category: 'Category' } as const;

// ---------------------------------------------------------------- one item

function ValueInput({
  input,
  disabled,
  onDecide,
}: {
  input: DecisionInput;
  disabled: boolean;
  onDecide: (label: string, decisions: ManualDecision[]) => void;
}) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string>();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const text = value.trim();
    if (input.kind === 'cost') {
      try {
        const costCents = parseMoney(text);
        if (costCents < 0) throw new RangeError('negative');
        onDecide(`Standard cost ${formatMoney(costCents)} per unit`, [
          { sourceRef: input.sourceRef, decision: { kind: 'set_cost', costCents } },
        ]);
        setError(undefined);
      } catch {
        setError('Enter an amount like 12.50.');
      }
      return;
    }
    if (!text) {
      setError(input.kind === 'gtin' ? 'Enter the barcode digits.' : 'Enter the SKU.');
      return;
    }
    if (input.kind === 'gtin' && !/^\d{8,14}$/.test(text)) {
      setError('A barcode is 8 to 14 digits.');
      return;
    }
    setError(undefined);
    onDecide(input.kind === 'gtin' ? `Barcode ${text}` : `SKU ${text}`, [
      input.kind === 'gtin'
        ? { sourceRef: input.sourceRef, decision: { kind: 'set_gtin', gtin: text } }
        : { sourceRef: input.sourceRef, decision: { kind: 'set_sku', sku: text } },
    ]);
  };
  const id = `input-${input.sourceRef}`;
  return (
    <form onSubmit={submit} className="flex flex-wrap items-end gap-2" noValidate>
      <div className="flex flex-col gap-1">
        <label htmlFor={id} className="text-sm font-medium text-fg">
          {input.label}
        </label>
        <Input
          id={id}
          value={value}
          inputMode={input.kind === 'sku' ? 'text' : 'decimal'}
          className={cn('w-44', input.kind !== 'sku' && 'tabular-nums')}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          disabled={disabled}
          onChange={(e) => setValue(e.target.value)}
        />
      </div>
      <Button type="submit" variant="secondary" disabled={disabled}>
        Use this
      </Button>
      {error && (
        <p id={`${id}-error`} role="alert" className="basis-full text-sm text-state-danger">
          {error}
        </p>
      )}
    </form>
  );
}

/**
 * The "another item…" picker (UI1A-5): searches the run's source items by name or SKU and
 * turns the chosen one into the option's decisions.
 */
function PickItemDialog({
  runId,
  option,
  onOpenChange,
  onPick,
}: {
  runId: string;
  option: DecisionOption & { pick: PickTarget };
  onOpenChange: (open: boolean) => void;
  onPick: (label: string, decisions: ManualDecision[]) => void;
}) {
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setQ(text.trim()), 250);
    return () => clearTimeout(timer);
  }, [text]);
  const targets = option.pick.componentTargets;
  const items = useQuery({ ...itemSearchQuery(runId, q), enabled: !targets && q.length >= 2 });
  // B-024: a component is resolved to a record that became a product recipes can use.
  const components = useQuery({
    ...componentTargetsQuery(runId, q),
    enabled: Boolean(targets) && q.length >= 2,
  });
  const search = targets ? components : items;
  // Only sensible targets: not the item itself, no recipe items, the right roles (UI1A-205).
  const found: {
    source_ref: string;
    name: string | null;
    sku: string | null;
    roles: readonly ProductRole[] | null;
    decisions: () => ManualDecision[];
  }[] = targets
    ? (components.data?.items ?? [])
        .filter((t) => !option.pick.exclude.includes(t.source_ref))
        .map((t) => ({ ...t, decisions: () => targets.decide(t) }))
    : (items.data?.items ?? [])
        .filter((o) => canPick(option.pick, o))
        .map((o) => ({
          source_ref: o.source_ref,
          name: o.name,
          sku: o.sku,
          roles: o.roles ?? null,
          decisions: () => option.pick.decide(o.source_ref),
        }));
  const inputId = useId();
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{option.label.replace(/…$/, '')}</DialogTitle>
          <DialogDescription>{option.pick.question}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <label htmlFor={inputId} className="text-sm font-medium text-fg">
            Search the items of this import
          </label>
          <div className="relative">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted"
            />
            <Input
              id={inputId}
              type="search"
              value={text}
              autoFocus
              placeholder="Name or SKU, at least 2 letters"
              className="pl-8"
              onChange={(e) => setText(e.target.value)}
            />
          </div>
          <div aria-live="polite" className="min-h-40">
            {q.length < 2 ? (
              <p className="text-sm text-muted">Type part of the name or SKU.</p>
            ) : search.isError ? (
              <p role="alert" className="text-sm text-state-danger">
                The search didn’t work. Check your connection and try again.
              </p>
            ) : search.isPending ? (
              <p className="text-sm text-muted">Searching…</p>
            ) : found.length === 0 ? (
              <p className="text-sm text-muted">No item matches “{q}”.</p>
            ) : (
              <ul
                aria-label="Matching items"
                className="flex max-h-72 flex-col gap-1 overflow-y-auto"
              >
                {found.map((o) => (
                  <li key={o.source_ref}>
                    <button
                      type="button"
                      className={cn(
                        'flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-body hover:bg-surface-hover',
                        focusRing,
                      )}
                      onClick={() => {
                        const name = o.name ?? 'the item';
                        onPick(option.pick.label(name), o.decisions());
                      }}
                    >
                      <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-fg" data-testid="pick-name">
                          {o.name ?? 'No name'}
                        </span>
                        {o.roles === null ? (
                          <Badge tone="outline">No role yet</Badge>
                        ) : (
                          ROLE_ORDER.filter((r) => o.roles?.includes(r)).map((r) => (
                            <Badge key={r} tone={r === 'sellable' ? 'primary' : 'neutral'}>
                              {ROLE_LABEL[r]}
                            </Badge>
                          ))
                        )}
                      </span>
                      {o.sku && (
                        <span className="shrink-0 font-mono text-sm text-muted">{o.sku}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** The records of a review item, with what tells them apart (UI1A-16). */
function SourceRecords({ item, runId }: { item: ImportReviewItem; runId: string }) {
  const categories = useQuery({
    ...categoriesQuery(runId),
    enabled: item.source_records.some((r) => r.record_kind === 'category'),
  });
  const itemsIn = new Map((categories.data?.items ?? []).map((c) => [c.source_ref, c.items]));
  const records = item.source_records;
  const category = records.find((r) => r.record_kind === 'category');
  // A category item names the category and, after it, an example or two of its items.
  const examples = category ? records.filter((r) => r !== category) : [];
  const shown = category ? [category] : records;
  // The categories of the records, when they differ (that is often why they need a decision).
  const cats = new Set(
    records.filter((r) => r.record_kind !== 'category').map((r) => r.category ?? null),
  );
  const showCategory = cats.size > 1;
  return (
    <ul aria-label="Source records" className="flex flex-col gap-1 text-sm">
      {shown.map((r) => {
        const n = r.record_kind === 'category' ? itemsIn.get(r.source_ref) : undefined;
        return (
          <li key={r.source_ref} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <Badge tone="outline">{RECORD_KIND[r.record_kind]}</Badge>
            <span className="text-fg">{r.name ?? 'Not in the source any more'}</span>
            {r.sku && <span className="font-mono text-muted">{r.sku}</span>}
            {showCategory && r.record_kind !== 'category' && (
              <Badge tone="neutral" data-testid="record-category">
                Category: {r.category ?? 'none'}
              </Badge>
            )}
            {r.record_kind === 'category' && (
              <span className="text-muted">
                {n === undefined
                  ? examples.length > 0 && 'e.g. '
                  : `${formatCount(n)} ${n === 1 ? 'item' : 'items'}${examples.length > 0 ? ', e.g. ' : ''}`}
                {examples.map((e) => `“${e.name ?? 'an item'}”`).join(', ')}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function ReviewCard({
  item,
  index,
  runId,
  pending,
  canEdit,
  locked,
  onDecide,
  onPick,
  onUndo,
  cardRef,
}: {
  item: ImportReviewItem;
  index: number;
  runId: string;
  pending: Pending | undefined;
  canEdit: boolean;
  locked: boolean;
  onDecide: (label: string, decisions: ManualDecision[]) => void;
  onPick: (option: DecisionOption & { pick: PickTarget }) => void;
  onUndo: () => void;
  cardRef: (el: HTMLLIElement | null) => void;
}) {
  const options = decisionOptions(item);
  const input = decisionInput(item);
  const titleId = `review-${index}-title`;
  const disabled = !canEdit || locked;
  // With one option there's nothing to suggest it over (UI1A-211).
  const marked = options.length > 1;
  return (
    <li
      ref={cardRef}
      tabIndex={0}
      data-review-card
      aria-labelledby={titleId}
      className={cn(
        'flex flex-col gap-3 rounded-lg border bg-surface-raised p-4 shadow-xs',
        pending ? 'border-state-success' : 'border-border',
        focusRing,
      )}
    >
      <p id={titleId} className="font-medium text-fg">
        {reviewItemText(item)}
      </p>
      <SourceRecords item={item} runId={runId} />
      {pending ? (
        <div
          role="status"
          className="flex flex-wrap items-center gap-3 rounded-md bg-state-success-subtle px-3 py-2 text-body text-fg"
        >
          <CircleCheck aria-hidden className="size-4 text-state-success" />
          <span className="min-w-0 flex-1">
            <span className="font-medium">Decided:</span> {pending.label}
            <span className="text-muted"> (not saved yet)</span>
          </span>
          <Button
            variant="ghost"
            size="sm"
            icon={<Undo2 aria-hidden />}
            onClick={onUndo}
            disabled={locked}
          >
            Undo
          </Button>
        </div>
      ) : (
        <>
          {options.length > 0 && (
            // Nothing is chosen until someone chooses: every option looks the same, the
            // suggestion is marked "Suggested" (UI1A-3).
            <div role="group" aria-label="Decisions" className="flex flex-wrap gap-2">
              {options.map((o, i) => (
                <Button
                  key={o.id}
                  variant="secondary"
                  size="sm"
                  disabled={disabled}
                  aria-keyshortcuts={i < 9 ? String(i + 1) : undefined}
                  aria-haspopup={o.pick ? 'dialog' : undefined}
                  title={o.decisions.map((d) => describeDecision(d, namesOf(item))).join('; ')}
                  className={cn(
                    marked && o.suggested && 'border-primary/60 ring-2 ring-primary/25',
                  )}
                  onClick={() =>
                    o.pick
                      ? onPick(o as DecisionOption & { pick: PickTarget })
                      : onDecide(o.label, o.decisions)
                  }
                >
                  {i < 9 && (
                    <kbd
                      aria-hidden
                      className="rounded border border-current/30 px-1 font-mono text-xs leading-4"
                    >
                      {i + 1}
                    </kbd>
                  )}
                  {o.label}
                  {marked && o.suggested && (
                    <Badge tone="primary" className="ml-0.5">
                      Suggested
                    </Badge>
                  )}
                </Button>
              ))}
            </div>
          )}
          {input && <ValueInput input={input} disabled={disabled} onDecide={onDecide} />}
        </>
      )}
    </li>
  );
}

// ---------------------------------------------------------------- bulk

function AcceptAllButton({
  kind,
  items,
  disabled,
  onAccept,
}: {
  kind: ReviewKind;
  items: ImportReviewItem[];
  disabled: boolean;
  onAccept: (decisions: { item: ImportReviewItem; decisions: ManualDecision[] }[]) => void;
}) {
  const withSuggestion = items
    .map((item) => ({ item, option: suggestionOf(item) }))
    .filter(
      (x): x is { item: ImportReviewItem; option: NonNullable<typeof x.option> } => !!x.option,
    );
  if (withSuggestion.length === 0) return null;
  const n = withSuggestion.length;
  const breakdown = suggestionBreakdown(withSuggestion.map((x) => x.option));
  const LISTED = 8;
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="secondary" size="sm" icon={<CheckCheck aria-hidden />} disabled={disabled}>
          Accept {n === 1 ? 'the suggestion' : `all ${formatCount(n)} suggestions`}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Accept {n === 1 ? 'the suggestion' : `${formatCount(n)} suggestions`} for “
            {REVIEW_KIND[kind].title}”?
          </AlertDialogTitle>
          <AlertDialogDescription>
            This records, as rules that every later import repeats:
          </AlertDialogDescription>
        </AlertDialogHeader>
        {/* What will be recorded, grouped, then the items themselves (UI1A-4). It scrolls, so
            the actions stay on screen (B-041). */}
        <AlertDialogBody className="gap-3 text-body">
          <ul aria-label="What will be recorded" className="flex flex-col gap-1">
            {breakdown.map((b) => (
              <li key={b.label} className="flex items-baseline justify-between gap-3">
                <span className="text-fg">{b.label}</span>
                <span className="shrink-0 tabular-nums text-muted">
                  {formatCount(b.count)} {b.count === 1 ? 'item' : 'items'}
                </span>
              </li>
            ))}
          </ul>
          <ul
            aria-label="Items"
            className="max-h-40 overflow-y-auto rounded-md border border-border bg-surface-sunken px-3 py-2 text-sm"
          >
            {withSuggestion.slice(0, LISTED).map(({ item, option }) => (
              <li key={item.key} className="py-0.5">
                <span className="text-fg">{namesOf(item)(item.source_refs[0] ?? '')}</span>
                <span className="text-muted"> — {option.label}</span>
              </li>
            ))}
            {n > LISTED && (
              <li className="py-0.5 text-muted">and {formatCount(n - LISTED)} more</li>
            )}
          </ul>
          <p className="text-sm text-muted">
            The import is checked again afterwards. You can take any of them back under “Decided
            earlier”.
          </p>
        </AlertDialogBody>
        <AlertDialogFooter>
          <AlertDialogCancel>Not now</AlertDialogCancel>
          <AlertDialogAction
            onClick={() =>
              onAccept(
                withSuggestion.map(({ item, option }) => ({ item, decisions: option.decisions })),
              )
            }
          >
            Accept and re-check
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ---------------------------------------------------------------- decided earlier

const DECIDED_PAGE = 50;

/** A stored decision's source name: from the run's records, else a readable fallback. */
function useRecordNames(runId: string, decisions: ManualDecision[]): (ref: string) => string {
  const refs = [
    ...new Set(
      decisions.flatMap((d) => {
        const x = d.decision;
        const partner =
          x.kind === 'pair_twin'
            ? x.partnerItemRef
            : x.kind === 'resolve_component'
              ? x.itemRef
              : null;
        return partner ? [d.sourceRef, partner] : [d.sourceRef];
      }),
    ),
  ];
  const records = refs.filter((r) => !r.startsWith('category:')).slice(0, IMPORT_PAGE_MAX);
  const outcomes = useQuery(outcomesByRefQuery(runId, records));
  const categories = useQuery({
    ...categoriesQuery(runId),
    enabled: refs.some((r) => r.startsWith('category:')),
  });
  const names = new Map<string, string>();
  for (const o of outcomes.data?.items ?? []) if (o.name) names.set(o.source_ref, o.name);
  for (const c of categories.data?.items ?? []) names.set(c.source_ref, c.name);
  return (ref) => {
    const name = names.get(ref);
    if (name) return `“${name}”`;
    const [kind] = ref.split(':');
    return kind === 'category' ? 'a category' : kind === 'variant' ? 'a variant' : 'an item';
  };
}

/**
 * The decisions already stored as rules (newest first), each of which can be taken back:
 * the run is checked again and the item returns to the queue.
 */
function DecidedList({
  runId,
  decisions,
  canEdit,
  locked,
  onRevoke,
  revoking,
}: {
  runId: string;
  decisions: ManualDecision[];
  canEdit: boolean;
  locked: boolean;
  onRevoke: (d: ManualDecision, label: string) => void;
  revoking: string | undefined;
}) {
  const [shown, setShown] = useState(DECIDED_PAGE);
  const newestFirst = [...decisions].reverse();
  const page = newestFirst.slice(0, shown);
  const name = useRecordNames(runId, page);
  if (decisions.length === 0) {
    return (
      <EmptyState
        compact
        icon={History}
        title="No decisions yet"
        description="Decisions you save from the queue are kept as rules and listed here, so you can take one back."
      />
    );
  }
  return (
    <section aria-labelledby="decided-title" className="flex min-w-0 flex-col gap-4">
      <div className="flex max-w-2xl flex-col gap-1">
        <h2 id="decided-title" className="font-display text-xl font-semibold text-fg">
          Decided earlier{' '}
          <span className="font-body text-base font-normal text-muted">
            ({formatCount(decisions.length)})
          </span>
        </h2>
        <p className="text-body text-muted">
          Every import repeats these. Take one back and the import is checked again without it; the
          item comes back to the queue to decide again.
        </p>
      </div>
      <ul aria-label="Decisions made earlier" className="flex flex-col gap-2">
        {page.map((d) => {
          const label = describeDecision(d, name);
          const key = `${d.sourceRef}|${d.decision.kind}`;
          return (
            <li
              key={key}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-surface-raised px-4 py-3"
            >
              <span className="min-w-0 flex-1 text-body text-fg">{label}</span>
              {canEdit && (
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<Undo2 aria-hidden />}
                  loading={revoking === key}
                  disabled={locked}
                  aria-label={`Take back: ${label}`}
                  onClick={() => onRevoke(d, label)}
                >
                  Take back
                </Button>
              )}
            </li>
          );
        })}
      </ul>
      {shown < newestFirst.length && (
        <Button
          variant="secondary"
          className="self-start"
          onClick={() => setShown((n) => n + DECIDED_PAGE)}
        >
          Show {formatCount(Math.min(DECIDED_PAGE, newestFirst.length - shown))} more
        </Button>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- page

function ReviewSkeleton() {
  return <LoadingSkeleton variant="page" label="Loading decisions" />;
}

function ReviewPage() {
  const { runId } = Route.useParams();
  const timeZone = useAccountTimeZone();
  const { kind: kindParam, view } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const { canEdit } = useImportPermissions();
  const run = useQuery(runQuery(runId));
  const review = useQuery(reviewItemsQuery(runId));
  const rules = useQuery(rulesQuery());
  const [revoking, setRevoking] = useState<string>();
  const [pending, setPending] = useState<Map<string, Pending>>(new Map());
  const [picking, setPicking] = useState<{
    item: ImportReviewItem;
    option: DecisionOption & { pick: PickTarget };
  }>();
  const cards = useRef<(HTMLLIElement | null)[]>([]);
  const nothingLeft = review.data?.items.length === 0;
  useDocumentTitle(nothingLeft ? 'All decided' : 'Decisions needed');

  // While the run re-plans, the list waits; afterwards it reloads (decided items are gone).
  const planning = run.data?.status === 'planning';
  const wasPlanning = useRef(planning);
  useEffect(() => {
    if (wasPlanning.current && !planning) {
      void queryClient.invalidateQueries({ queryKey: importKeys.reviewItems(runId, undefined) });
    }
    wasPlanning.current = planning;
  }, [planning, queryClient, runId]);

  const save = useMutation({
    mutationFn: (entries: { item: ImportReviewItem; decisions: ManualDecision[] }[]) =>
      postDecisions(
        runId,
        entries.flatMap(({ item, decisions }) =>
          decisions.map((d) => ({
            review_item_key: item.key,
            source_ref: d.sourceRef,
            decision: d.decision,
          })),
        ),
      ),
    onSuccess: async (next: ImportRunDetail, entries) => {
      queryClient.setQueryData(importKeys.run(runId), next);
      wasPlanning.current = true;
      setPending((old) => {
        const copy = new Map(old);
        for (const { item } of entries) copy.delete(item.key);
        return copy;
      });
      toast.success(
        `${formatCount(entries.length)} ${entries.length === 1 ? 'decision' : 'decisions'} saved`,
        { description: 'Saved as rules. The import is being checked again.' },
      );
      await invalidateRun(queryClient, runId);
    },
    onError: async (e, entries) => {
      if (e instanceof DecisionBatchError) {
        // Part-way (B-023): the saved ones leave the list of unsaved changes.
        let left = e.saved;
        const done = new Set<string>();
        for (const { item, decisions } of entries) {
          if (decisions.length > left) break;
          left -= decisions.length;
          done.add(item.key);
        }
        setPending((old) => {
          const copy = new Map(old);
          for (const key of done) copy.delete(key);
          return copy;
        });
        toast.error('Not every decision was saved', {
          description: `${e.message} Save again to send the rest.`,
        });
      } else {
        const text = apiErrorText(e, 'the source', 'The decisions weren’t saved');
        toast.error(text.title, { description: text.message });
      }
      await invalidateRun(queryClient, runId);
    },
  });

  // Takes a stored decision back (`revoke`): the run is checked again without it.
  const revoke = useMutation({
    mutationFn: (d: ManualDecision) =>
      postDecisions(runId, [{ source_ref: d.sourceRef, revoke: d.decision.kind }]),
    onMutate: (d) => setRevoking(`${d.sourceRef}|${d.decision.kind}`),
    onSuccess: async (next: ImportRunDetail) => {
      queryClient.setQueryData(importKeys.run(runId), next);
      wasPlanning.current = true;
      toast.success('Decision taken back', {
        description: 'The import is being checked again; the item comes back to the queue.',
      });
      await invalidateRun(queryClient, runId);
    },
    onError: async (e) => {
      const text = apiErrorText(e, 'the source', 'The decision wasn’t taken back');
      toast.error(text.title, { description: text.message });
      await invalidateRun(queryClient, runId);
    },
    onSettled: () => setRevoking(undefined),
  });

  if (run.isPending || review.isPending) return <ReviewSkeleton />;
  const failed = run.error ?? review.error;
  if (failed) {
    return (
      <div className="max-w-2xl">
        <ErrorPanel
          error={failed}
          what="The decisions"
          onRetry={() => {
            void run.refetch();
            void review.refetch();
          }}
        />
      </div>
    );
  }

  if (!run.data || !review.data) return <ReviewSkeleton />;
  const r = run.data;
  const all = review.data.items;
  const byKind = new Map<ReviewKind, ImportReviewItem[]>();
  for (const item of all) byKind.set(item.kind, [...(byKind.get(item.kind) ?? []), item]);
  const kinds = REVIEW_ORDER.filter((k) => byKind.has(k));
  const kind = kindParam && byKind.has(kindParam) ? kindParam : kinds[0];
  const items = kind ? (byKind.get(kind) ?? []) : [];
  const reviewable =
    r.status === 'awaiting_review' || r.status === 'ready' || r.status === 'planning';
  const locked = save.isPending || revoke.isPending || isBusy(r.status);
  const stored = rules.data?.rules.decisions ?? [];
  const showDecided = view === 'decided' && reviewable;

  const decide = (item: ImportReviewItem, label: string, decisions: ManualDecision[]) => {
    setPending((old) => new Map(old).set(item.key, { label, decisions }));
    // On to the next item still open.
    const at = items.indexOf(item);
    const next = items.findIndex((x, i) => i > at && !pending.has(x.key));
    cards.current[next >= 0 ? next : at]?.focus();
  };
  const undo = (item: ImportReviewItem) =>
    setPending((old) => {
      const copy = new Map(old);
      copy.delete(item.key);
      return copy;
    });
  const pendingEntries = all
    .filter((i) => pending.has(i.key))
    .map((item) => ({ item, decisions: (pending.get(item.key) as Pending).decisions }));
  const saveAll = () => {
    if (pendingEntries.length > 0 && !locked) save.mutate(pendingEntries);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLOListElement>) => {
    if (e.ctrlKey || e.metaKey || e.altKey || isTypingTarget(e.target)) return;
    const at = cards.current.findIndex((c) => c?.contains(document.activeElement ?? null));
    if (e.key === 'j' || e.key === 'ArrowDown') {
      e.preventDefault();
      cards.current[Math.min(items.length - 1, at + 1)]?.focus();
    } else if (e.key === 'k' || e.key === 'ArrowUp') {
      e.preventDefault();
      cards.current[Math.max(0, at - 1)]?.focus();
    } else if (/^[1-9]$/.test(e.key) && at >= 0 && canEdit && !locked) {
      const item = items[at] as ImportReviewItem;
      if (pending.has(item.key)) return;
      const option = decisionOptions(item)[Number(e.key) - 1];
      if (option) {
        e.preventDefault();
        if (option.pick)
          setPicking({ item, option: option as DecisionOption & { pick: PickTarget } });
        else decide(item, option.label, option.decisions);
      }
    } else if (e.key === 's' && pendingEntries.length > 0) {
      e.preventDefault();
      saveAll();
    }
  };

  let body: ReactNode;
  if (!reviewable) {
    body = (
      <EmptyState
        icon={CircleCheck}
        title="This import no longer takes decisions"
        description="Decisions can be made while an import waits for review or a load. Start a new import to change them."
        action={
          <Button asChild>
            <Link to="/imports/runs/$runId" params={{ runId }}>
              Back to the report
            </Link>
          </Button>
        }
      />
    );
  } else if (all.length === 0 && !planning && !showDecided) {
    body = (
      <EmptyState
        icon={CircleCheck}
        // Green only when the report passes too (UI1A-212).
        tone={r.report_status === 'pass' ? 'success' : undefined}
        title="Nothing needs a decision"
        description={
          r.status === 'ready' && r.report_status === 'pass'
            ? 'Every record is accounted for and every check passed. The import is ready to load.'
            : r.status === 'ready'
              ? 'Every item is decided, but not every check passed. The report says which, and who can load it.'
              : 'Every item is decided.'
        }
        action={
          <div className="flex flex-wrap justify-center gap-3">
            <Button asChild>
              <Link to="/imports/runs/$runId" params={{ runId }}>
                Back to the report
              </Link>
            </Button>
            {stored.length > 0 && (
              <Button variant="secondary" asChild>
                <Link
                  to="/imports/runs/$runId/review"
                  params={{ runId }}
                  search={{ view: 'decided' }}
                >
                  <History aria-hidden />
                  Decisions made earlier
                </Link>
              </Button>
            )}
          </div>
        }
      />
    );
  } else {
    body = (
      <div className="grid gap-6 lg:grid-cols-[15rem_minmax(0,1fr)]">
        <nav
          aria-label="Kinds of decision"
          className="flex flex-col gap-1 self-start lg:sticky lg:top-4"
        >
          {kinds.map((k) => {
            const n = byKind.get(k)?.length ?? 0;
            const decided = (byKind.get(k) ?? []).filter((i) => pending.has(i.key)).length;
            return (
              <Link
                key={k}
                to="/imports/runs/$runId/review"
                params={{ runId }}
                search={{ kind: k }}
                aria-current={k === kind && !showDecided ? 'page' : undefined}
                className={cn(
                  'flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm',
                  k === kind && !showDecided
                    ? 'bg-primary-subtle font-medium text-primary-text'
                    : 'text-fg hover:bg-surface-hover',
                  focusRing,
                )}
              >
                <span>{REVIEW_KIND[k].title}</span>
                <span className="tabular-nums text-muted">
                  {decided > 0 ? `${formatCount(decided)}/` : ''}
                  {formatCount(n)}
                </span>
              </Link>
            );
          })}
          <Link
            to="/imports/runs/$runId/review"
            params={{ runId }}
            search={{ view: 'decided' }}
            aria-current={showDecided ? 'page' : undefined}
            className={cn(
              'mt-2 flex items-center justify-between gap-2 rounded-md border-t border-border px-3 py-2 text-sm',
              showDecided
                ? 'bg-primary-subtle font-medium text-primary-text'
                : 'text-fg hover:bg-surface-hover',
              focusRing,
            )}
          >
            <span className="flex items-center gap-2">
              <History aria-hidden className="size-4" />
              Decided earlier
            </span>
            <span className="tabular-nums text-muted">{formatCount(stored.length)}</span>
          </Link>
        </nav>
        {showDecided ? (
          <DecidedList
            runId={runId}
            decisions={stored}
            canEdit={canEdit}
            locked={locked}
            revoking={revoking}
            onRevoke={(d) => revoke.mutate(d)}
          />
        ) : (
          <section aria-labelledby="kind-title" className="flex min-w-0 flex-col gap-4">
            {kind && (
              <div className="flex min-w-0 max-w-2xl flex-col gap-1">
                <h2 id="kind-title" className="font-display text-xl font-semibold text-fg">
                  {REVIEW_KIND[kind].title}{' '}
                  <span className="font-body text-base font-normal text-muted">
                    ({formatCount(items.length)})
                  </span>
                </h2>
                <p className="text-body text-muted">{REVIEW_KIND[kind].explain}</p>
              </div>
            )}
            {/* The group action sits in the same place for every kind, however long the
                explanation above runs (UI1A-211). */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="flex items-center gap-2 text-sm text-muted">
                <Keyboard aria-hidden className="size-4" />
                <span>
                  <kbd className="font-mono">j</kbd>/<kbd className="font-mono">k</kbd> or arrows to
                  move · <kbd className="font-mono">1</kbd>–<kbd className="font-mono">9</kbd> to
                  decide · <kbd className="font-mono">s</kbd> to save
                </span>
              </p>
              {canEdit && kind && (
                <AcceptAllButton
                  kind={kind}
                  items={items.filter((i) => !pending.has(i.key))}
                  disabled={locked}
                  onAccept={(entries) => save.mutate(entries)}
                />
              )}
            </div>
            {canEdit &&
              items.some((i) => decisionOptions(i).some((o) => o.suggested && o.attests)) && (
                <p className="text-sm text-muted">
                  No “accept all” here: each decision records that the baker will check that recipe,
                  so it’s made one item at a time.
                </p>
              )}
            <ol
              aria-label={kind ? REVIEW_KIND[kind].title : 'Decisions'}
              className="flex flex-col gap-3"
              onKeyDown={onKeyDown}
            >
              {items.map((item, i) => (
                <ReviewCard
                  key={item.key}
                  item={item}
                  index={i}
                  runId={runId}
                  pending={pending.get(item.key)}
                  canEdit={canEdit}
                  locked={locked}
                  onDecide={(label, decisions) => decide(item, label, decisions)}
                  onPick={(option) => setPicking({ item, option })}
                  onUndo={() => undo(item)}
                  cardRef={(el) => {
                    cards.current[i] = el;
                  }}
                />
              ))}
            </ol>
          </section>
        )}
      </div>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow={
          <ImportsBreadcrumb run={{ id: runId, label: runLabel(r, timeZone) }} page="Decisions" />
        }
        title={all.length === 0 && reviewable && !planning ? 'All decided' : 'Decisions needed'}
        description={
          all.length > 0
            ? `${formatCount(all.length)} ${all.length === 1 ? 'item' : 'items'} to decide. Each decision is kept as a rule, so the next import repeats it.`
            : 'Each decision is kept as a rule, so the next import repeats it.'
        }
        actions={
          <Button variant="secondary" asChild>
            <Link to="/imports/runs/$runId" params={{ runId }}>
              <ArrowLeft aria-hidden />
              Back to the report
            </Link>
          </Button>
        }
      />
      {(planning || save.isPending || revoke.isPending) && (
        <Note icon={Loader2} tone="info" role="status" className="mb-4">
          <p className="font-medium">Re-checking…</p>
          <p className="text-muted">
            Your decisions are saved. The import is being checked again; the list updates by itself.
          </p>
        </Note>
      )}
      {/* Room under the last card for the sticky save bar (tablet, UI1A-15). */}
      <div className={cn(pendingEntries.length > 0 && 'pb-24')}>{body}</div>
      {picking && (
        <PickItemDialog
          runId={runId}
          option={picking.option}
          onOpenChange={(open) => {
            if (!open) setPicking(undefined);
          }}
          onPick={(label, decisions) => {
            const item = picking.item;
            setPicking(undefined);
            decide(item, label, decisions);
          }}
        />
      )}
      {pendingEntries.length > 0 && (
        <div
          role="region"
          aria-label="Decisions to save"
          data-sticky-bar
          className="sticky bottom-4 z-10 ml-auto mt-6 flex w-fit flex-wrap items-center gap-3 rounded-lg border border-border bg-surface-raised px-4 py-3 shadow-lg"
        >
          {/* A field focused under the bar scrolls clear of it (B-030). */}
          <RoomForStickyBar />
          <p className="text-body text-fg" aria-live="polite">
            {formatCount(pendingEntries.length)}{' '}
            {pendingEntries.length === 1 ? 'decision' : 'decisions'} ready to save
          </p>
          <Button onClick={saveAll} loading={save.isPending} disabled={locked && !save.isPending}>
            {save.isPending ? 'Saving…' : 'Save and re-check'}
          </Button>
          <Button variant="ghost" onClick={() => setPending(new Map())} disabled={save.isPending}>
            Clear
          </Button>
        </div>
      )}
      {/* Keeps the kind in the URL valid after a re-check emptied it. */}
      {!showDecided && kindParam && kind !== kindParam && kinds.length > 0 && (
        <RedirectKind
          to={kind}
          onGo={(k) => void navigate({ search: { kind: k }, replace: true })}
        />
      )}
    </>
  );
}

function RedirectKind({ to, onGo }: { to: ReviewKind | undefined; onGo: (k: ReviewKind) => void }) {
  useEffect(() => {
    if (to) onGo(to);
  }, [to, onGo]);
  return null;
}
